import { useEffect, useRef, useState } from 'react'
import { Loader2, Minus, Plus, Settings2, X, Zap } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/i18n'
import { useAppStore } from '@/store'
import { Checkbox } from '@/components/ui/checkbox'
import { IntuiAvatar } from '@/components/intueri/IntuiAvatar'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import {
  IntuiSelect,
  IntuiSelectContent,
  IntuiSelectItem,
  IntuiSelectTrigger,
  IntuiSelectValue,
} from '@/components/intueri/IntuiSelect'
import { IntuiTextarea } from '@/components/intueri/IntuiTextarea'
import { AGENT_ALIASES, AgentId, getAgentAvatarImage } from '@/constants/agentNames'
import { apiService } from '@/service/api'
import { enhanceWorkflowInstructions } from '../workflowApi'
import {
  notifySettingsChanged,
  getWorkflowSettings,
  hydrateWorkflowSettings,
  knowledgeFileName,
  notifyPersonName,
  subscribeWorkflowSettings,
  updateWorkflowSettings,
  type WorkflowAutonomy,
  type WorkflowSettings,
} from '../workflowSettings'
import { setSnakeRowsPerColumn, snakeRowsPerColumn } from '../workflowGraph'

type SettingsTab = 'configure' | 'limits'

/** The AI engineer personas selectable to execute a workflow. */
const ENGINEERS = [
  AgentId.PLANT_SUPPORT,
  AgentId.MECHANICAL,
  AgentId.CHEMICAL,
  AgentId.ELECTRICAL,
]
  .map((id) => AGENT_ALIASES[id]?.displayName)
  .filter((name): name is string => Boolean(name))

const FREQUENCIES = ['1 hour', '12 hours', '1 day', '1 week']

const SECTION_LABEL = 'text-sm font-medium text-foreground'

/** Autonomy levels (Figma "10. Update" — Configure-tab dropdown). */
const AUTONOMY_OPTIONS: ReadonlyArray<{
  value: WorkflowAutonomy
  labelKey: string
  label: string
  descKey: string
  desc: string
}> = [
  {
    value: 'review_every_step',
    labelKey: 'workflow:settings.autonomy_every',
    label: 'Review every step',
    descKey: 'workflow:settings.autonomy_every_hint',
    desc: 'AI pauses before each action and waits for approval.',
  },
  {
    value: 'review_critical',
    labelKey: 'workflow:settings.autonomy_critical',
    label: 'Review critical actions',
    descKey: 'workflow:settings.autonomy_critical_hint',
    desc: 'AI runs on its own but pauses only for high risk actions.',
  },
  {
    value: 'dangerously_bypass',
    labelKey: 'workflow:settings.autonomy_bypass',
    label: 'Dangerously bypass',
    descKey: 'workflow:settings.autonomy_bypass_hint',
    desc: 'AI completes the full task on its own, without approval.',
  },
]

/**
 * Workflow settings panel (Figma "7. Settings"), docked right of the canvas.
 * Configure: instructions (+ Enhance), AI engineers, knowledge files,
 * frequency/time, max steps, Generate Workflow. Limits: notification people and
 * spend limits. Everything persists per-workflow via workflowSettings
 * (client-side until the scheduler/limits backend lands); Generate feeds the
 * instructions to the real draft generator.
 */
export default function SettingsPanel({
  workflowId,
  onClose,
  onGenerate,
  onUploadFiles,
  generating = false,
}: {
  workflowId: string
  onClose: () => void
  /** Generate the workflow from the Configure instructions (real NL generation). */
  onGenerate: (instructions: string) => void
  /** Open the Select Files modal; picked names land in knowledgeFiles. */
  onUploadFiles: () => void
  generating?: boolean
}) {
  const { t } = useTranslation()
  const [tab, setTab] = useState<SettingsTab>('configure')
  const [enhancing, setEnhancing] = useState(false)
  const [settings, setSettings] = useState<WorkflowSettings>(() => getWorkflowSettings(workflowId))
  const [snakeRows, setSnakeRows] = useState(() => snakeRowsPerColumn())
  const [addingPerson, setAddingPerson] = useState(false)
  const [personQuery, setPersonQuery] = useState('')
  // Email-notification recipient picker (Configure tab, below Max Steps).
  const [addingNotifyPerson, setAddingNotifyPerson] = useState(false)
  const [notifyQuery, setNotifyQuery] = useState('')
  const notifyPickerRef = useRef<HTMLDivElement | null>(null)
  const closeNotifyPicker = () => {
    setAddingNotifyPerson(false)
    setNotifyQuery('') // reopen starts fresh, not on a stale filter
  }
  // Click-outside dismissal: the picker is an INLINE dropdown with no overlay,
  // so without this the only exits were Escape (undiscoverable) and actually
  // picking someone — clicking anywhere else left it stuck open.
  useEffect(() => {
    if (!addingNotifyPerson) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target
      if (
        notifyPickerRef.current &&
        target instanceof Node &&
        !notifyPickerRef.current.contains(target)
      ) {
        setAddingNotifyPerson(false)
        setNotifyQuery('')
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [addingNotifyPerson])
  const [tenantUsers, setTenantUsers] = useState<
    Array<{ name: string | null; email: string | null; picture: string | null }>
  >([])
  const loadTenantUsers = async () => {
    if (tenantUsers.length > 0) return
    try {
      const { users } = await apiService.getOrganizationUsers()
      setTenantUsers(users.filter((user) => user.status !== 'invited'))
    } catch {
      // Picker shows the loading row; retry on next open.
    }
  }

  // The instructions the workflow was last generated from (or opened with):
  // the Generate button stays disabled until the text DIFFERS from this
  // baseline, so "changed instructions" is what arms it (Putra "10. Update").
  const instructionsBaselineRef = useRef('')

  // Re-read when the panel is pointed at a different workflow: render the
  // cached value immediately, then reconcile with the server row.
  useEffect(() => {
    let active = true
    const cached = getWorkflowSettings(workflowId)
    setSettings(cached)
    instructionsBaselineRef.current = cached.instructions.trim()
    void hydrateWorkflowSettings(workflowId).then((server) => {
      if (!active) return
      // Re-baseline on hydration only if the user hasn't edited meanwhile.
      if (instructionsBaselineRef.current === cached.instructions.trim()) {
        instructionsBaselineRef.current = server.instructions.trim()
      }
      setSettings(server)
    })
    // Files attach from OUTSIDE this panel too (chat "+" → Attach File, the
    // builder dialog): without this subscription the Knowledge list only
    // refreshed on reopen — attached files "didn't show".
    const unsubscribe = subscribeWorkflowSettings((changedId) => {
      if (active && changedId === workflowId) setSettings(getWorkflowSettings(workflowId))
    })
    return () => {
      active = false
      unsubscribe()
    }
  }, [workflowId])

  const patch = (update: Partial<WorkflowSettings>) =>
    setSettings(updateWorkflowSettings(workflowId, update))

  // The Autonomy option backing the select trigger (single lookup per render).
  const selectedAutonomy =
    AUTONOMY_OPTIONS.find((o) => o.value === settings.autonomy) ?? AUTONOMY_OPTIONS[0]

  // Notifications default to the current user ("Myself") — Putra 2026-07-15.
  // Seed once per workflow (ref-guarded) only when the recipient list is
  // empty, so a deliberate clear within the session sticks. The current user
  // comes from the JWT session (authSessionSlice.user).
  const currentUser = useAppStore((state) => state.user)
  const notifySeededRef = useRef<string | null>(null)
  useEffect(() => {
    if (notifySeededRef.current === workflowId) return
    if (!currentUser?.email) return
    if (getWorkflowSettings(workflowId).notifyPeople.length > 0) {
      // Already has recipients (persisted) — don't override; just mark seeded.
      notifySeededRef.current = workflowId
      return
    }
    notifySeededRef.current = workflowId
    setSettings(
      updateWorkflowSettings(workflowId, {
        notifyPeople: [{ name: currentUser.name || currentUser.email, email: currentUser.email }],
      })
    )
  }, [workflowId, currentUser])

  // Read the persisted value at click time (not the render snapshot) so two
  // quick toggles can't clobber each other through a stale closure.
  const toggleEngineer = (name: string) => {
    const engineers = getWorkflowSettings(workflowId).engineers
    patch({
      engineers: engineers.includes(name)
        ? engineers.filter((e) => e !== name)
        : [...engineers, name],
    })
  }

  // Enhance: rewrite the instructions with the native synthesizer, keeping
  // the user's text when the model is unavailable or has nothing to add.
  const enhance = async () => {
    const original = getWorkflowSettings(workflowId).instructions.trim()
    if (!original || enhancing) return
    setEnhancing(true)
    try {
      const { enhanced } = await enhanceWorkflowInstructions(original)
      if (enhanced) {
        patch({ instructions: enhanced })
      } else {
        toast.info(
          t('workflow:settings.enhance_no_change', {
            defaultValue: 'No enhancement available — your instructions were kept as-is.',
          })
        )
      }
    } catch {
      toast.error(
        t('workflow:settings.enhance_failed', { defaultValue: 'Could not enhance instructions' })
      )
    } finally {
      setEnhancing(false)
    }
  }

  const addPerson = () => {
    const name = personQuery.trim()
    if (!name) return
    const notifyPeople = getWorkflowSettings(workflowId).notifyPeople
    if (!notifyPeople.includes(name)) {
      patch({ notifyPeople: [...notifyPeople, name] })
    }
    setPersonQuery('')
    setAddingPerson(false)
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Tabs header: Configure | Limits + close */}
      <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
        <div className="inline-flex items-center gap-0.5 rounded-lg bg-muted p-0.5 dark:bg-dark-muted">
          {(
            [
              ['configure', Settings2, t('workflow:settings.configure', { defaultValue: 'Configure' })],
              ['limits', Zap, t('workflow:settings.limits', { defaultValue: 'Limits' })],
            ] as const
          ).map(([value, Icon, label]) => {
            // Limits DISABLED until token-based pricing lands (the current
            // spend caps estimate USD from token counts with a placeholder
            // rate; surfacing dollar limits before real pricing misleads).
            // The enforcement plumbing stays intact server-side.
            const disabled = value === 'limits'
            return (
              <button
                key={value}
                type="button"
                disabled={disabled}
                title={
                  disabled
                    ? t('workflow:settings.limits_disabled_hint', {
                        defaultValue: 'Coming soon — enabled once token-based pricing is available.',
                      })
                    : undefined
                }
                onClick={() => setTab(value)}
                className={cn(
                  'flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium transition-colors',
                  tab === value
                    ? 'bg-background text-foreground shadow-sm dark:bg-dark-card'
                    : 'text-muted-foreground hover:text-foreground',
                  disabled && 'cursor-not-allowed opacity-40 hover:text-muted-foreground'
                )}
              >
                <Icon className="size-4" />
                {label}
              </button>
            )
          })}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common:close', { defaultValue: 'Close' })}
          className="grid size-7 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </div>

      {tab === 'configure' ? (
        <>
          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overflow-x-hidden p-4 [&>*]:shrink-0">
            {/* Instructions + Enhance + Generate: its own SECTION, closed by a
                divider (Putra "10. Update": "Instruction should be separate
                section"). */}
            <div className="flex flex-col gap-5 border-b border-border pb-5">
            <div className="flex flex-col gap-2">
              <span className={SECTION_LABEL}>
                {t('workflow:settings.instructions', { defaultValue: 'Instructions' })}
                <span className="text-red-500">*</span>
              </span>
              <div className="relative">
                <IntuiTextarea
                  value={settings.instructions}
                  onChange={(e) => patch({ instructions: e.target.value })}
                  minRows={5}
                  placeholder={t('workflow:settings.instructions_placeholder', {
                    defaultValue:
                      'Every morning, review overnight plant alerts, run a root-cause analysis on any critical alarms, and summarize findings for the shift lead.',
                  })}
                  className="pb-10"
                />
                <button
                  type="button"
                  disabled={enhancing || !settings.instructions.trim()}
                  onClick={() => void enhance()}
                  className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 rounded-md bg-foreground px-2.5 py-1 text-xs font-medium text-background disabled:opacity-60"
                >
                  {/* UI-29 (Pegah): "I think this icon is over-used in
                      vibe-coded apps and makes our product feel less enterprise
                      grade." The sparkle shared this slot with the in-flight
                      spinner, which is the only feedback the button gives while
                      the enhancer runs — so the slot stays, it just holds
                      nothing at rest. */}
                  {enhancing ? <Loader2 className="size-3 animate-spin" aria-hidden /> : null}
                  {t('workflow:settings.enhance', { defaultValue: 'Enhance' })}
                </button>
              </div>
            </div>

            {/* Generate Workflow — secondary, directly under the instruction
                field (Putra 2026-07-15). Enabled only once the user CHANGES
                the instructions (Putra "10. Update": "When user do some
                changes on instruction will be enabled") — regenerating an
                unchanged prompt is a no-op. */}
            <div className="flex justify-end">
              <IntuiButton
                variant="outline"
                size="sm"
                /* UI-29: no leftIcon. IntuiButton's `loading` still renders its
                   own spinner, so the busy state is unaffected. */
                loading={generating}
                disabled={
                  !settings.instructions.trim() ||
                  settings.instructions.trim() === instructionsBaselineRef.current
                }
                onClick={() => {
                  instructionsBaselineRef.current = settings.instructions.trim()
                  onGenerate(settings.instructions)
                }}
              >
                {t('workflow:settings.generate', { defaultValue: 'Generate Workflow' })}
              </IntuiButton>
            </div>
            </div>

            {/* AI engineers */}
            <div className="flex flex-col gap-2">
              <span className={SECTION_LABEL}>
                {t('workflow:settings.select_engineer', { defaultValue: 'Select AI Engineer' })}
              </span>
              <div className="flex flex-col gap-1.5">
                {ENGINEERS.map((name) => {
                  const checked = settings.engineers.includes(name)
                  return (
                    <label
                      key={name}
                      className={cn(
                        'flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2 text-sm transition-colors',
                        checked ? 'border-border bg-muted/60 dark:bg-dark-muted/60' : 'border-border'
                      )}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => toggleEngineer(name)} />
                      <IntuiAvatar
                        src={getAgentAvatarImage(name)}
                        alt={name}
                        fallback={name.slice(0, 2)}
                        size="xs"
                      />
                      <span className="text-foreground">{name}</span>
                    </label>
                  )
                })}
              </div>
            </div>

            {/* Autonomy (Figma "10. Update"): run-time review posture — how
                often the run pauses for human approval. */}
            <div className="flex flex-col gap-2">
              <span className={SECTION_LABEL}>
                {t('workflow:settings.autonomy', { defaultValue: 'Autonomy' })}
              </span>
              <IntuiSelect
                value={settings.autonomy}
                onValueChange={(v) => patch({ autonomy: v as WorkflowAutonomy })}
              >
                <IntuiSelectTrigger>
                  {t(selectedAutonomy.labelKey, { defaultValue: selectedAutonomy.label })}
                </IntuiSelectTrigger>
                <IntuiSelectContent>
                  {AUTONOMY_OPTIONS.map((opt) => (
                    <IntuiSelectItem key={opt.value} value={opt.value}>
                      <span className="flex flex-col gap-0.5">
                        <span>
                          {t(opt.labelKey, { defaultValue: opt.label })}
                          {/* Figma "10. Update" marks the default level inline in
                              the open list (the trigger stays the plain label). */}
                          {opt.value === AUTONOMY_OPTIONS[0].value && (
                            <span className="text-muted-foreground">
                              {' '}
                              {t('workflow:settings.autonomy_default_marker', {
                                defaultValue: '(default)',
                              })}
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {t(opt.descKey, { defaultValue: opt.desc })}
                        </span>
                      </span>
                    </IntuiSelectItem>
                  ))}
                </IntuiSelectContent>
              </IntuiSelect>
            </div>

            {/* Knowledge */}
            <div className="flex flex-col gap-2">
              <span className={SECTION_LABEL}>
                {t('workflow:settings.knowledge', { defaultValue: 'Knowledge' })}
              </span>
              <p className="text-sm text-muted-foreground">
                {t('workflow:settings.knowledge_hint', {
                  defaultValue: 'Upload or select reference files the AI can use.',
                })}
              </p>
              <div>
                <IntuiButton variant="outline" size="sm" onClick={onUploadFiles}>
                  {t('workflow:settings.upload_files', { defaultValue: 'Upload files' })}
                </IntuiButton>
              </div>
              {settings.knowledgeFiles.length > 0 && (
                <ul className="flex flex-col gap-1.5">
                  {settings.knowledgeFiles.map((entry) => {
                    const file = knowledgeFileName(entry)
                    // documentId is the identity when present — removing one
                    // of two same-named catalog docs must not delete both.
                    const identity = typeof entry === 'object' ? (entry.documentId ?? entry.name) : entry
                    return (
                    <li
                      key={identity}
                      className="flex min-w-0 items-center gap-2 overflow-hidden rounded-lg border border-border px-3 py-2 text-sm text-foreground"
                    >
                      <span className="min-w-0 flex-1 truncate" title={file}>{file}</span>
                      <button
                        type="button"
                        aria-label={t('workflow:settings.remove_file', { defaultValue: 'Remove {{name}}', name: file })}
                        onClick={() =>
                          patch({
                            knowledgeFiles: settings.knowledgeFiles.filter(
                              (f) =>
                                (typeof f === 'object' ? (f.documentId ?? f.name) : f) !== identity
                            ),
                          })
                        }
                        className="grid size-5 place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        <Minus className="size-3.5" />
                      </button>
                    </li>
                    )
                  })}
                </ul>
              )}
            </div>

            {/* Frequency + Time */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-2">
                <span className={SECTION_LABEL}>
                  {t('workflow:settings.frequency', { defaultValue: 'Frequency' })}
                </span>
                <IntuiSelect value={settings.frequency} onValueChange={(v) => patch({ frequency: v })}>
                  <IntuiSelectTrigger>
                    <IntuiSelectValue />
                  </IntuiSelectTrigger>
                  <IntuiSelectContent>
                    {FREQUENCIES.map((f) => (
                      <IntuiSelectItem key={f} value={f}>
                        {f}
                      </IntuiSelectItem>
                    ))}
                  </IntuiSelectContent>
                </IntuiSelect>
              </div>
              <div className="flex flex-col gap-2">
                <span className={SECTION_LABEL}>
                  {t('workflow:settings.time', { defaultValue: 'Time' })}
                </span>
                <IntuiInput
                  type="time"
                  value={settings.time}
                  onChange={(e) => patch({ time: e.target.value })}
                />
              </div>
            </div>

            {/* Max steps + Nodes density (Figma "10. Update": one row, like
                Frequency + Time). Nodes density is a client canvas preference,
                not tenant data; its notify re-lays the graph immediately. */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-2">
                <span className={SECTION_LABEL}>
                  {t('workflow:settings.max_steps', { defaultValue: 'Max Steps' })}
                </span>
                <IntuiInput
                  type="number"
                  min={1}
                  value={String(settings.maxSteps)}
                  onChange={(e) => {
                    const parsed = Number.parseInt(e.target.value, 10)
                    if (Number.isFinite(parsed) && parsed > 0) patch({ maxSteps: parsed })
                  }}
                />
              </div>
              <div className="flex flex-col gap-2">
                <span className={SECTION_LABEL}>
                  {t('workflow:settings.nodes_density', { defaultValue: 'Nodes density' })}
                </span>
                <IntuiSelect
                  value={String(snakeRows)}
                  onValueChange={(v) => {
                    const rows = Number.parseInt(v, 10)
                    setSnakeRows(rows)
                    setSnakeRowsPerColumn(rows)
                    notifySettingsChanged(workflowId)
                  }}
                >
                  <IntuiSelectTrigger>
                    <IntuiSelectValue />
                  </IntuiSelectTrigger>
                  <IntuiSelectContent>
                    {[3, 4, 5, 6, 7, 8].map((rows) => (
                      <IntuiSelectItem key={rows} value={String(rows)}>
                        {t('workflow:settings.density_rows', {
                          defaultValue: `${rows} nodes per column`,
                          count: rows,
                        })}
                      </IntuiSelectItem>
                    ))}
                  </IntuiSelectContent>
                </IntuiSelect>
              </div>
            </div>

            {/* "Send email to" (Figma node 14674:25286): run-event emails —
                parked runs needing input, failures, completion — to selected
                tenant users. No recipients = no emails. */}
            <div className="flex flex-col gap-2">
              <span className={SECTION_LABEL}>
                {t('workflow:settings.send_email_to', { defaultValue: 'Send email to' })}
              </span>
              {settings.notifyPeople.length > 0 ? (
                <ul className="flex flex-wrap gap-1.5">
                  {settings.notifyPeople.map((entry) => {
                    const name = notifyPersonName(entry)
                    const email = typeof entry === 'object' ? entry.email : undefined
                    // Identity is the EMAIL when present (two users can share
                    // a display name; legacy free-text entries have only the
                    // name) — keys, dedupe, and removal all use it so a
                    // remove never deletes a same-named other person.
                    const identity = email ?? name
                    // Figma "10. Update": the current user's row reads
                    // "Name (Myself)" so the default recipient is obvious.
                    const isSelf = Boolean(email && email === currentUser?.email)
                    return (
                      <li
                        key={identity}
                        title={email ?? name}
                        className="flex items-center gap-1.5 rounded-md border border-border bg-muted/30 px-2 py-1 text-xs text-foreground"
                      >
                        <IntuiAvatar alt={name} fallback={name.slice(0, 2)} size="xs" />
                        <span className="max-w-[180px] truncate">
                          {isSelf
                            ? t('workflow:settings.person_myself', {
                                defaultValue: `${name} (Myself)`,
                                name,
                              })
                            : name}
                        </span>
                        {/* The current user is the permanent recipient — the
                            run owner must always hear about parked/failed/
                            finished runs, so their chip offers no remove. */}
                        {!isSelf && (
                          <button
                            type="button"
                            aria-label={t('workflow:settings.remove_person', { defaultValue: 'Remove {{name}}', name })}
                            onClick={() =>
                              patch({
                                notifyPeople: settings.notifyPeople.filter(
                                  (p) =>
                                    (typeof p === 'object' ? (p.email ?? p.name) : p) !== identity
                                ),
                              })
                            }
                            className="grid size-4 place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                          >
                            <Minus className="size-3" />
                          </button>
                        )}
                      </li>
                    )
                  })}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t('workflow:settings.notifications_off', {
                    defaultValue:
                      'No recipients — email notifications are off for this workflow. Selected people get an email when a run needs input, fails, or completes.',
                  })}
                </p>
              )}
              {/* The add-button stays visible and TOGGLES: while the picker is
                  open it reads "Close" (X) — together with Escape and
                  click-outside there are three ways out, where before the
                  button vanished and the open picker had no visible exit. */}
              <div ref={notifyPickerRef} className="flex flex-col gap-1.5">
                <IntuiButton
                  variant="outline"
                  size="sm"
                  className="self-start"
                  leftIcon={addingNotifyPerson ? <X /> : <Plus />}
                  aria-label={
                    addingNotifyPerson
                      ? t('workflow:settings.close_person_picker_aria', {
                          defaultValue: 'Close person picker',
                        })
                      : undefined
                  }
                  onClick={() => {
                    if (addingNotifyPerson) {
                      closeNotifyPicker()
                      return
                    }
                    setAddingNotifyPerson(true)
                    void loadTenantUsers()
                  }}
                >
                  {addingNotifyPerson
                    ? t('workflow:settings.close_person_picker', { defaultValue: 'Close' })
                    : t('workflow:settings.add_person', { defaultValue: 'Add person' })}
                </IntuiButton>
                {addingNotifyPerson && (
                  <>
                  <IntuiInput
                    autoFocus
                    placeholder={t('workflow:settings.find_user', { defaultValue: 'Search tenant users…' })}
                    value={notifyQuery}
                    onChange={(e) => setNotifyQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') closeNotifyPicker()
                    }}
                  />
                  <ul className="max-h-40 overflow-y-auto rounded-lg border border-border">
                    {tenantUsers
                      .filter(
                        (user) =>
                          user.email &&
                          // Already-added is judged by EMAIL — a legacy
                          // free-text entry sharing the name must not
                          // suppress the real user.
                          !settings.notifyPeople.some(
                            (p) => typeof p === 'object' && p.email === user.email
                          ) &&
                          `${user.name ?? ''} ${user.email ?? ''}`
                            .toLowerCase()
                            .includes(notifyQuery.trim().toLowerCase())
                      )
                      .slice(0, 8)
                      .map((user) => (
                        <li key={user.email ?? user.name ?? ''}>
                          {/* email is non-null here (the filter above gates on
                              it); the ?? chain just spells that out for the
                              key. Avatar + name only; the email reveals on
                              hover (title) so the row stays clean. */}
                          <button
                            type="button"
                            title={user.email ?? undefined}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                            onClick={() => {
                              patch({
                                notifyPeople: [
                                  ...settings.notifyPeople,
                                  { name: user.name ?? user.email!, email: user.email! },
                                ],
                              })
                              closeNotifyPicker()
                            }}
                          >
                            <IntuiAvatar
                              src={user.picture ?? undefined}
                              alt={user.name ?? user.email ?? ''}
                              fallback={(user.name ?? user.email ?? '?').slice(0, 2)}
                              size="xs"
                            />
                            <span className="min-w-0 truncate">{user.name ?? user.email}</span>
                          </button>
                        </li>
                      ))}
                    {tenantUsers.length === 0 && (
                      <li className="px-3 py-2 text-xs text-muted-foreground">
                        {t('workflow:settings.loading_users', { defaultValue: 'Loading tenant users…' })}
                      </li>
                    )}
                  </ul>
                  </>
                )}
              </div>
            </div>

          </div>
        </>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overflow-x-hidden p-4 [&>*]:shrink-0">
          {/* Notification */}
          <section className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className={SECTION_LABEL}>
                {t('workflow:settings.notification', { defaultValue: 'Notification' })}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">
                {t('workflow:settings.error_budget_alerts', { defaultValue: 'Error & Budget Alerts' })}
              </span>
              <button
                type="button"
                aria-label={t('workflow:settings.add_person', { defaultValue: 'Add person' })}
                onClick={() => setAddingPerson((v) => !v)}
                className="grid size-6 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Plus className="size-4" />
              </button>
            </div>
            {/* TODO(notify-unification): this legacy free-text input stores a
                bare name with NO email — such entries are never emailed (the
                engine skips them). The whole Limits tab is disabled pending
                token-based pricing; when it revives, replace this input with
                the Configure tab's tenant-user picker. */}
            {addingPerson && (
              <IntuiInput
                autoFocus
                value={personQuery}
                onChange={(e) => setPersonQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addPerson()
                  }
                  if (e.key === 'Escape') {
                    setAddingPerson(false)
                    setPersonQuery('')
                  }
                }}
                placeholder={t('workflow:settings.find_user', { defaultValue: 'Find user' })}
              />
            )}
            <ul className="flex flex-col gap-1.5">
              {settings.notifyPeople.map((entry) => {
                const person = notifyPersonName(entry)
                // Email is the identity when present — same rule as the
                // Configure-tab chips, so removal never hits a same-named
                // other recipient.
                const identity = typeof entry === 'object' ? (entry.email ?? entry.name) : entry
                // Same rule as the Configure-tab chips: the current user is
                // the permanent recipient and cannot be removed.
                const isSelf = Boolean(
                  typeof entry === 'object' && entry.email && entry.email === currentUser?.email
                )
                return (
                <li
                  key={identity}
                  className="flex min-w-0 items-center gap-2 overflow-hidden rounded-lg border border-border px-3 py-2 text-sm text-foreground"
                >
                  <IntuiAvatar alt={person} fallback={person.slice(0, 2)} size="xs" />
                  <span className="min-w-0 flex-1 truncate">{person}</span>
                  {!isSelf && (
                    <button
                      type="button"
                      aria-label={t('workflow:settings.remove_person', { defaultValue: 'Remove {{name}}', name: person })}
                      onClick={() =>
                        patch({
                          notifyPeople: settings.notifyPeople.filter(
                            (p) =>
                              (typeof p === 'object' ? (p.email ?? p.name) : p) !== identity
                          ),
                        })
                      }
                      className="grid size-5 place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <Minus className="size-3.5" />
                    </button>
                  )}
                </li>
                )
              })}
            </ul>
          </section>

          {/* Limits */}
          <section className="flex flex-col gap-3 border-t border-border pt-4">
            <div className="flex items-center justify-between">
              <span className={SECTION_LABEL}>
                {t('workflow:settings.limits', { defaultValue: 'Limits' })}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm text-foreground">
                {t('workflow:settings.monthly_limit', { defaultValue: 'Limits workflow spend (monthly)' })}
              </span>
              <MoneyInput
                value={settings.limits.workflowSpendUsd}
                onChange={(value) => patch({ limits: { ...settings.limits, workflowSpendUsd: value } })}
              />
              <p className="text-xs text-muted-foreground">
                {t('workflow:settings.monthly_limit_hint', {
                  defaultValue: 'Workflow will be turned off when this limit hits',
                })}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground">
                  {t('workflow:settings.notify_at', { defaultValue: 'Notify when monthly usage hits' })}
                </span>
                <button
                  type="button"
                  aria-label={t('workflow:settings.add_threshold', { defaultValue: 'Add threshold' })}
                  onClick={() =>
                    patch({ limits: { ...settings.limits, notifyAtUsd: [...settings.limits.notifyAtUsd, 10] } })
                  }
                  className="grid size-6 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <Plus className="size-4" />
                </button>
              </div>
              {settings.limits.notifyAtUsd.map((amount, index) => (
                // Composite key: notifyAtUsd is a plain number[] (duplicates legal),
                // so index+value keeps rows stable across middle deletions.
                <div key={`${index}-${amount}`} className="flex items-center gap-2">
                  <MoneyInput
                    value={amount}
                    onChange={(value) => {
                      const next = [...settings.limits.notifyAtUsd]
                      next[index] = value ?? 0
                      patch({ limits: { ...settings.limits, notifyAtUsd: next } })
                    }}
                  />
                  <button
                    type="button"
                    aria-label={t('workflow:settings.remove_threshold', { defaultValue: 'Remove threshold' })}
                    onClick={() =>
                      patch({
                        limits: {
                          ...settings.limits,
                          notifyAtUsd: settings.limits.notifyAtUsd.filter((_, i) => i !== index),
                        },
                      })
                    }
                    className="grid size-6 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <Minus className="size-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm text-foreground">
                {t('workflow:settings.per_run_limit', { defaultValue: 'Limits per workflow spend' })}
              </span>
              <MoneyInput
                value={settings.limits.perRunSpendUsd}
                onChange={(value) => patch({ limits: { ...settings.limits, perRunSpendUsd: value } })}
              />
            </div>
          </section>
        </div>
      )}
    </div>
  )
}

/** "$N" input: renders with the $ prefix, stores a number (null when empty). */
function MoneyInput({
  value,
  onChange,
}: {
  value: number | null
  onChange: (value: number | null) => void
}) {
  return (
    <div className="relative w-full">
      <span className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
        $
      </span>
      <IntuiInput
        type="number"
        min={0}
        value={value === null ? '' : String(value)}
        onChange={(e) => {
          const parsed = Number.parseFloat(e.target.value)
          onChange(Number.isFinite(parsed) ? parsed : null)
        }}
        className="ps-7"
      />
    </div>
  )
}
